import { AxiosError, AxiosResponse } from 'axios'
import { useQuery } from 'react-query'

import * as requests from './requests'
import {
  Apnef,
  ContractsAmountDataSerialized,
  CreditInsuranceDataSerialized,
  Fpd10DataSerialized,
  GetChartDataParams,
  GetChartDataParamsSequencial,
  MarketShareDataSerialized,
  OpenedAccountQuantityDataSerialized,
  Over30M3DataSerialized,
  PotentialDataSerialized,
  ProductionDataSerialized,
  ProposalFunnelSerialized,
  SantanderAutoInsuranceDataSerialized,
  SantanderAutoTypeDataSerialized,
  SpreadPercentageDataSerialized,
  WebmotorsDataSerialized
} from './types'
import { ResponseError, ResponseSuccess } from '../../types'

export const KEY_PROPOSAL_FUNNEL_QUERY = 'getProposalFunnel'

export const KEY_APNEF_QUERY = 'getApnef'

export const KEY_CONTRACTS_AMOUNT_QUERY = 'getContractsAmount'

export const KEY_SPREAD_PERCENTAGE_QUERY = 'getSpreadPercentage'

export const KEY_CREDIT_INSURANCE_QUERY = 'getCreditInsurance'

export const KEY_SANTANDER_AUTO_TYPE_QUERY = 'getSantanderAutoType'

export const KEY_PRODUCTION_QUERY = 'getProduction'

export const KEY_POTENTIAL_QUERY = 'getPotential'

export const KEY_MARKET_SHARE_QUERY = 'getMarketShare'

export const KEY_SANTANDER_AUTO_INSURANCE_QUERY = 'getSantanderAutoInsurance'

export const KEY_OPENED_ACCOUNT_QUANTITY_QUERY = 'getOpenedAccountQuantity'

export const KEY_WEBMOTORS_QUERY = 'getWebmotors'

export const KEY_FPD10_QUERY = 'getFpd10'

export const KEY_OVER30_M3_QUERY = 'getOver30M3'

const buildPeriodKey = (key: string, params: GetChartDataParams) =>
  `${key}.${params.tab_number}.${params.year_month ?? ''}.${params.year_month_qty ?? '6'}`

export const useGetApnefQuery = (params: GetChartDataParams) =>
  useQuery<AxiosResponse<ResponseSuccess<Apnef>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_APNEF_QUERY, params)],
    () => requests.getApnef(params)
  )

export const useGetProposalFunnelQuery = (params: GetChartDataParams) =>
  useQuery<AxiosResponse<ResponseSuccess<ProposalFunnelSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_PROPOSAL_FUNNEL_QUERY, params)],
    () => requests.getProposalFunnel(params)
  )

export const useGetProductionQuery = (params: GetChartDataParams) =>
  useQuery<AxiosResponse<ResponseSuccess<ProductionDataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_PRODUCTION_QUERY, params)],
    () => requests.getProduction({ ...params })
  )

export const useGetPotentialQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<AxiosResponse<ResponseSuccess<PotentialDataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_POTENTIAL_QUERY, params)],
    () => requests.getPotential({ ...params }),
    { enabled: params.enabled }
  )

export const useGetMarketShareQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<AxiosResponse<ResponseSuccess<MarketShareDataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_MARKET_SHARE_QUERY, params)],
    () => requests.getMarketShare({ ...params }),
    { enabled: params.enabled }
  )

export const useGetSantanderAutoInsuranceQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<
    AxiosResponse<ResponseSuccess<SantanderAutoInsuranceDataSerialized>>,
    AxiosError<ResponseError>
  >(
    [buildPeriodKey(KEY_SANTANDER_AUTO_INSURANCE_QUERY, params)],
    () => requests.getSantanderAutoInsurance({ ...params }),
    { enabled: params.enabled }
  )

export const useGetOpenedAccountQuantityQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<
    AxiosResponse<ResponseSuccess<OpenedAccountQuantityDataSerialized>>,
    AxiosError<ResponseError>
  >(
    [buildPeriodKey(KEY_OPENED_ACCOUNT_QUANTITY_QUERY, params)],
    () => requests.getOpenedAccountQuantity({ ...params }),
    { enabled: params.enabled }
  )

export const useGetWebmotorsQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<AxiosResponse<ResponseSuccess<WebmotorsDataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_WEBMOTORS_QUERY, params)],
    () => requests.getWebmotors({ ...params }),
    { enabled: params.enabled }
  )

export const useGetFpd10Query = (params: GetChartDataParamsSequencial) =>
  useQuery<AxiosResponse<ResponseSuccess<Fpd10DataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_FPD10_QUERY, params)],
    () => requests.getFpd10({ ...params }),
    { enabled: params.enabled }
  )

export const useGetOver30M3Query = (params: GetChartDataParamsSequencial) =>
  useQuery<AxiosResponse<ResponseSuccess<Over30M3DataSerialized>>, AxiosError<ResponseError>>(
    [buildPeriodKey(KEY_OVER30_M3_QUERY, params)],
    () => requests.getOver30M3({ ...params }),
    { enabled: params.enabled }
  )

export const useGetContractsAmountQuery = (params: GetChartDataParams) =>
  useQuery<
    AxiosResponse<ResponseSuccess<ContractsAmountDataSerialized>>,
    AxiosError<ResponseError>
  >([buildPeriodKey(KEY_CONTRACTS_AMOUNT_QUERY, params)], () =>
    requests.getContractsAmount({ ...params })
  )

export const useGetSpreadPercentageQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<
    AxiosResponse<ResponseSuccess<SpreadPercentageDataSerialized>>,
    AxiosError<ResponseError>
  >(
    [buildPeriodKey(KEY_SPREAD_PERCENTAGE_QUERY, params)],
    () => requests.getSpreadPercentage({ ...params }),
    { enabled: params.enabled }
  )

export const useGetCreditInsuranceQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<
    AxiosResponse<ResponseSuccess<CreditInsuranceDataSerialized>>,
    AxiosError<ResponseError>
  >(
    [buildPeriodKey(KEY_CREDIT_INSURANCE_QUERY, params)],
    () => requests.getCreditInsurance({ ...params }),
    { enabled: params.enabled }
  )

export const useGetSantanderAutoTypeQuery = (params: GetChartDataParamsSequencial) =>
  useQuery<
    AxiosResponse<ResponseSuccess<SantanderAutoTypeDataSerialized>>,
    AxiosError<ResponseError>
  >(
    [buildPeriodKey(KEY_SANTANDER_AUTO_TYPE_QUERY, params)],
    () => requests.getSantanderAutoType({ ...params }),
    { enabled: params.enabled }
  )
